// Filter strategies — maps column.filter.type (the public contract of
// DataGrid) onto TanStack Table's filterFn signature. Each strategy
// receives the row, columnId, and filterValue (as TanStack stores it in
// state) and returns true/false.
//
// We also expose `resolveFilterMeta` which packages the filter config
// for the header popover to consume (the popover only sees TanStack's
// meta, not the original column.filter).

const isEmpty = value =>
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && (value.from ?? '') === '' && (value.to ?? '') === '');

const normalize = value => String(value ?? '').toLowerCase();

// ─── Range parsing ─────────────────────────────────────────────────
// Shared by the numberRange and dateRange strategies below.

// Normalize a range endpoint to either a non-empty string or null. Empty
// strings would otherwise survive into the numeric comparisons and JS
// would coerce them to 0 (`number > ""` → `number > 0`), filtering
// everything out of a min-only or max-only range.
const normalizeRangeEndpoint = value =>
    value === '' || value === null || value === undefined ? null : value;

const parseRange = filterValue => {
    // The filter can arrive as an object ({ from, to }) when set via the
    // popover, or as a string ("from..to") when restored from the URL.
    if (typeof filterValue === 'string') {
        const [from, to] = filterValue.split('..');
        return {
            from: normalizeRangeEndpoint(from),
            to: normalizeRangeEndpoint(to)
        };
    }
    return {
        from: normalizeRangeEndpoint(filterValue?.from),
        to: normalizeRangeEndpoint(filterValue?.to)
    };
};

// ─── Date helpers ──────────────────────────────────────────────────

// Threshold between unix seconds and milliseconds, expressed as ms.
// 1e12 ms lands at 2001-09-09 — every plausible "seconds" timestamp
// (10-digit) is well below it; every plausible "ms" timestamp (13-
// digit) is well above. Used by `toMilliseconds` to coerce a numeric
// row value into ms without each row carrying its own unit hint.
const SECONDS_TO_MS_THRESHOLD = 1e12;

// Convert a row value to milliseconds-since-epoch for date comparisons.
// Handles three shapes consumers might hand us:
//   - number ≥ SECONDS_TO_MS_THRESHOLD: already milliseconds.
//   - number  < SECONDS_TO_MS_THRESHOLD: unix seconds (WiseMetering API
//     convention) — multiply by 1000.
//   - string: an ISO-ish date string that `new Date(...)` can parse.
const toMilliseconds = value => {
    if (typeof value === 'number') {
        return value < SECONDS_TO_MS_THRESHOLD ? value * 1000 : value;
    }
    return new Date(value).getTime();
};

// Strict yyyy-mm-dd regex. Partial input ("2024" / "2024-05") and any
// freeform garbage stays out of the comparison — endpoints that don't
// match are treated as "no bound" on that side, so the user typing
// into the popover doesn't wipe the dataset mid-keystroke.
const YYYY_MM_DD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// ─── Strategies ────────────────────────────────────────────────────

const filterText = (row, columnId, filterValue) => {
    if (isEmpty(filterValue)) {
        return true;
    }
    return normalize(row.getValue(columnId)).includes(normalize(filterValue));
};

// Select is always multi-capable: the popover renders our Select primitive
// with `isMulti=true`, so the user can pick one or several values out of
// the same widget. The filter value is therefore an array (or a comma-
// separated string when restored from the URL) — picking exactly one item
// is just a 1-element array.
const filterSelect = (row, columnId, filterValue) => {
    if (isEmpty(filterValue)) {
        return true;
    }
    const cellValue = String(row.getValue(columnId));
    const selectedValues = Array.isArray(filterValue)
        ? filterValue
        : String(filterValue).split(',').filter(Boolean);
    return selectedValues.some(value => String(value) === cellValue);
};

const filterNumberRange = (row, columnId, filterValue) => {
    if (isEmpty(filterValue)) {
        return true;
    }
    const { from, to } = parseRange(filterValue);
    const cellValue = Number(row.getValue(columnId));
    if (Number.isNaN(cellValue)) {
        return false;
    }
    if (from !== null && cellValue < from) {
        return false;
    }
    if (to !== null && cellValue > to) {
        return false;
    }
    return true;
};

const filterDateRange = (row, columnId, filterValue) => {
    if (isEmpty(filterValue)) {
        return true;
    }
    const { from, to } = parseRange(filterValue);
    const cellValue = toMilliseconds(row.getValue(columnId));
    if (Number.isNaN(cellValue)) {
        return false;
    }
    // `from` is start of day (00:00); `to` is end of day (23:59:59.999)
    // so the filter is inclusive on both edges — picking "to 2024-05-15"
    // includes events that happened at any time on that day. Endpoints
    // that don't match yyyy-mm-dd are dropped silently.
    const fromMs = from !== null && YYYY_MM_DD_PATTERN.test(from)
        ? new Date(`${from}T00:00:00`).getTime()
        : null;
    const toMs = to !== null && YYYY_MM_DD_PATTERN.test(to)
        ? new Date(`${to}T23:59:59.999`).getTime()
        : null;
    if (fromMs !== null && cellValue < fromMs) {
        return false;
    }
    if (toMs !== null && cellValue > toMs) {
        return false;
    }
    return true;
};

const STRATEGIES = {
    text: filterText,
    select: filterSelect,
    numberRange: filterNumberRange,
    dateRange: filterDateRange
};

const buildFilterFn = filterConfig => STRATEGIES[filterConfig.type] ?? filterText;

const resolveFilterMeta = filterConfig => ({
    type: filterConfig.type,
    options: filterConfig.options ?? null,
    placeholder: filterConfig.placeholder ?? null
});

export { buildFilterFn, resolveFilterMeta };
